import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ActionType,
  convertToISO,
  DayIntervalUnit,
  getDeviceAtTimeFromInstant,
  getScheduleDefaultInstant,
  getWallTimeInstant,
  parseMMDDYYYYToDate,
  RoutineType,
  RoutineWorkflow,
  SCHEDULE_DEFAULTS,
  ScheduleDefault,
  TriggerType,
  WorkspaceAsset,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { router } from 'expo-router';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Platform,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { number, string, object as yupObject } from 'yup';
import { Colors } from '../../../themes/colors';
import { Button } from '../../crop-circle/Button/Button';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import { fontWeights } from '../../crop-circle/tokens/typography';
import { ControlledSelect, DatePickerSelector, FormContext } from '../../forms';
import { KeyboardSafeView } from '../../keyboard/keyboardSafeView';
import { BottomComponentBar } from '../../navigation';
import AlertLogicPicker from './alertLogicPicker';
import {
  DROPDOWN_MAX_HEIGHT,
  getCropCircleDropDownPickerProps,
} from './cropCircleAlertDropdown';
import { AlertInputs, AlertType } from './types';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

type AlertFormProps = {
  defaultValues: any;
  routine?: WorkspaceRoutine;
  workspaceAsset?: WorkspaceAsset;
  product?: { id: string; workspace?: { id: string } };
  group?: { id: string; workspace?: { id: string } };
  groupAssets?: Array<{
    id: string;
    asset: { id: string; name: string };
    routineState?: Array<{
      workspaceRoutineId: string;
      state: any;
    }>;
  }>;
  counterRoutines: WorkspaceRoutine[];
  availableRoutines?: WorkspaceRoutine[];
  workflowProps?: RoutineWorkflow;
  isEdit?: boolean;
  headerTitle?: string;
  backButtonLabel?: string;
  openDropdownOnLoad?: boolean;
  renderBottomComponent?: (
    onSubmit: () => void,
    disabled: boolean
  ) => React.ReactNode; // For custom bottom component (e.g., SlideLayout confirm button)
  /** Ref to get handleSubmit and reset from outside (avoids putting confirm button inside form and triggering max update depth) */
  formRef?: React.MutableRefObject<{
    handleSubmit: (fn: (data: any) => Promise<void>) => () => void;
    reset: () => void;
  } | null>;
  /** Called when isValid/isSubmitting/isValidating change so parent can drive a confirm button without form context */
  onFormStateChange?: (state: {
    isValid: boolean;
    isSubmitting: boolean;
    isValidating: boolean;
  }) => void;
  /**
   * Called on mount with a guard function. Call that function with an `onProceed` callback
   * to check if the form has unsaved changes — if so, the user is prompted and `onProceed`
   * is invoked only when confirmed; otherwise `onProceed` is called immediately.
   */
  onRegisterBackGuard?: (
    guard: (onProceed: () => void, onCancel?: () => void) => void
  ) => void;
  submitAlert: (input: any) => Promise<void>;
  deleteAlert?: () => Promise<void>;
  onClose?: () => void;
};

// The alert shapes the API's supportsAutoReset can act on for a single asset.
const AUTO_RESET_ALERT_TYPES: (AlertType | undefined)[] = [
  AlertType.RecurringInterval,
  AlertType.Interval,
  AlertType.RoutineCompletions,
];

/**
 * Which schedule default applies to the currently selected alert logic, if
 * any. DAYS/WEEKS/MONTHS recurring alerts and one-time (onDate) alerts get a
 * time-of-day picker; MINUTES/HOURS intervals don't (the backend ignores
 * atTime for those). alertType/secondAlertType can independently hold either
 * kind via the "OR" logic picker — recurring takes priority in that overlap,
 * since a single trigger.config only ever carries one atTime.
 */
const getActiveScheduleDefault = (
  alertType: AlertType | undefined,
  secondAlertType: AlertType | undefined,
  dayIntervalUnit: DayIntervalUnit | undefined
): ScheduleDefault | undefined => {
  const isDayLevelRecurring =
    (alertType === AlertType.RecurringInterval ||
      secondAlertType === AlertType.RecurringInterval) &&
    dayIntervalUnit !== DayIntervalUnit.Minutes &&
    dayIntervalUnit !== DayIntervalUnit.Hours;
  if (isDayLevelRecurring) return SCHEDULE_DEFAULTS.recurringAlert;

  const isOneTimeDate =
    alertType === AlertType.Date || secondAlertType === AlertType.Date;
  if (isOneTimeDate) return SCHEDULE_DEFAULTS.oneTime;

  return undefined;
};

export const AlertForm = ({
  defaultValues,
  routine,
  workspaceAsset,
  product,
  group,
  groupAssets,
  counterRoutines,
  availableRoutines,
  workflowProps,
  isEdit = false,
  headerTitle,
  backButtonLabel,
  openDropdownOnLoad,
  renderBottomComponent,
  formRef,
  onFormStateChange,
  onRegisterBackGuard,
  submitAlert,
  deleteAlert,
  onClose,
}: AlertFormProps) => {
  const { t } = useTranslation();
  const [additionalLogic, setAdditionalLogic] = useState(false);
  const [showRoutinesDropdown, setShowRoutinesDropdown] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const initialAssetIntervalStarts: Record<string, number> = useMemo(
    () => workflowProps?.trigger?.config?.assetIntervalStarts ?? {},
    [workflowProps]
  );
  const workspaceId =
    workspaceAsset?.workspace?.id ||
    product?.workspace?.id ||
    group?.workspace?.id;
  const workspaceAssetId = workspaceAsset?.id;
  const productId = product?.id;
  const groupId = group?.id;
  const MIN_REMINDER_MINUTES = 15;
  const isGroupAlert = !!groupId;
  // Entity-level alerts (group, product) arrive with no implied routine. The
  // routine is what the trigger fires on: without it the saved config carries
  // no workspaceRoutineId/routineType, and both the scheduled-alert sweep and
  // the counter evaluators skip the workflow entirely — a silently dead alert.
  // A group is held to this even with nothing to pick (GroupAlertForm shows an
  // empty state rather than this form, so it is not a dead end); the product
  // form has no such empty state, so there it only applies once there is
  // something selectable.
  const requiresRoutineSelection =
    !routine && (isGroupAlert || !!availableRoutines?.length);

  const isValidDate = (value?: string) => {
    if (!value) return true;
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    if (!regex.test(value)) return false;
    const [month, day, year] = value.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const formSchema = yupObject({
    selectedRoutine: requiresRoutineSelection
      ? string().required(t('alerts.validation.routineRequired'))
      : string().optional(),
    alertType: string()
      .required()
      .min(1, t('alerts.validation.alertTypeRequired')),
    inputField: string()
      .required()
      .min(1, t('alerts.validation.inputRequired')),
    secondAlertType: string().optional(),
    secondInputField: string()
      .required()
      .when('secondAlertType', {
        is: (val: any) => val !== undefined,
        otherwise: (schema) => schema.optional(),
      }),
    alertLabel: string().optional(),
    alertLabelPlaceholder: string().optional(),
    startDate: string()
      .optional()
      .test('is-valid-date', t('alerts.validation.invalidDate'), isValidDate),
    endDate: string()
      .optional()
      .test('is-valid-date', t('alerts.validation.invalidDate'), isValidDate),
    minValue: number().optional(),
    maxValue: number().optional(),
    daysStartDate: string()
      .nullable()
      .when(['alertType', 'secondAlertType'], {
        is: (val: string) =>
          val === AlertType.RecurringInterval ||
          secondAlertType === AlertType.RecurringInterval,
        then: (schema) =>
          schema.required(t('alerts.validation.startDateRequiredForDays')),
        otherwise: (schema) => schema.optional(),
      }),
    completions: number().optional(),
    daysSkipped: number().optional(),
    onDate: string().optional(),
    dayInterval: string().when(['alertType', 'dayIntervalUnit'], {
      is: (alertType: string, dayIntervalUnit: string) => {
        return (
          alertType === AlertType.RecurringInterval &&
          dayIntervalUnit === DayIntervalUnit.Minutes
        );
      },
      then: (schema) =>
        schema
          .required('Interval is required')
          .test('min-minutes', 'Must be at least 15 minutes', (value) => {
            const numValue = parseInt(value || '0', 10);
            return numValue >= MIN_REMINDER_MINUTES;
          }),
      otherwise: (schema) =>
        schema.when('alertType', {
          is: (val: string) => val === AlertType.RecurringInterval,
          then: (schema) =>
            schema
              .required('Interval is required')
              .test('min-interval', 'Must be at least 1', (value) => {
                const numValue = parseInt(value || '0', 10);
                return numValue >= 1;
              }),
          otherwise: (schema) => schema.optional(),
        }),
    }),
    dayIntervalUnit: string().when('alertType', {
      is: (val: string) => val === AlertType.RecurringInterval,
      then: (schema) => schema.required('Interval unit is required'),
      otherwise: (schema) => schema.optional(),
    }),
    onInterval: number().optional(),
    onIntervalStart: number().optional(),
    intervalRoutineId: string()
      .nullable()
      .when(['alertType', 'secondAlertType'], {
        is: (alertType: string, secondType: string) => {
          const counterBasedTypes = [
            AlertType.Interval,
            AlertType.Min,
            AlertType.Max,
          ];
          return (
            counterBasedTypes.includes(alertType as AlertType) ||
            counterBasedTypes.includes(secondType as AlertType)
          );
        },
        then: (schema) =>
          schema.required(t('alerts.validation.routineIdRequiredForInterval')),
        otherwise: (schema) => schema.optional(),
      }),
  });

  // Create: onTouched so isValid updates when user fills fields (Confirm disabled until valid).
  // Edit: defaultValues so form is editable and confirm/reset work (parent uses key={alertId} so we get correct initial data per alert).
  // Auto Reset starts on for a new alert; an edit form passes the stored value.
  const formDefaults = useMemo(
    () => ({ [AlertInputs.autoReset]: true, ...(defaultValues ?? {}) }),
    [defaultValues]
  );

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: isEdit ? 'onChange' : 'onTouched',
    defaultValues: formDefaults,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid, isSubmitting, isValidating },
    setValue,
    getValues,
    trigger,
  } = formMethods;

  // Expose submit and reset to parent ref (useLayoutEffect so ref is set before header can be used)
  const resetToDefaults = useCallback(() => {
    reset(formDefaults);
    setAdditionalLogic(!!formDefaults.secondAlertType);
    setShowRoutinesDropdown(false);
    setIsDropdownOpen(false);
  }, [reset, formDefaults]);

  useLayoutEffect(() => {
    if (!formRef) return;
    formRef.current = { handleSubmit, reset: resetToDefaults };
    return () => {
      formRef.current = null;
    };
  }, [formRef, handleSubmit, resetToDefaults]);

  // Register the back guard once on mount so external callers can intercept back actions
  useEffect(() => {
    onRegisterBackGuard?.((onProceed, onCancel) => {
      if (formMethods.formState.isDirty) {
        Alert.alert(
          t('common.unsavedChanges'),
          t('common.unsavedChangesMessage'),
          [
            {
              text: t('common.cancel'),
              style: 'cancel',
              onPress: () => onCancel?.(),
            },
            {
              text: t('common.discard'),
              style: 'destructive',
              onPress: () => onProceed(),
            },
          ]
        );
      } else {
        onProceed();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify parent only when validity/loading change. Defer to next tick to avoid
  // "Cannot update a component while rendering a different component" (RHF validation runs during Controller render).
  const prevState = React.useRef<{
    isValid: boolean;
    isSubmitting: boolean;
    isValidating: boolean;
  } | null>(null);
  useEffect(() => {
    if (!onFormStateChange) return;
    const next = { isValid, isSubmitting, isValidating };
    if (
      prevState.current &&
      prevState.current.isValid === next.isValid &&
      prevState.current.isSubmitting === next.isSubmitting &&
      prevState.current.isValidating === next.isValidating
    ) {
      return;
    }
    prevState.current = next;
    const timeoutId = setTimeout(() => onFormStateChange(next), 0);
    return () => clearTimeout(timeoutId);
  }, [isValid, isSubmitting, isValidating, onFormStateChange]);

  const alertType = useWatch({ control, name: 'alertType' });
  const selectedRoutineId = useWatch({ control, name: 'selectedRoutine' });

  // Use selected routine from form, fallback to passed routine
  const currentRoutine =
    availableRoutines && selectedRoutineId
      ? availableRoutines.find((r) => r.id === selectedRoutineId) || routine
      : routine;

  const workspaceRoutineId = currentRoutine?.id;
  const routineType = currentRoutine?.type;
  const routineName = currentRoutine?.config?.routineButtonText;
  const defaultAlertName = routineName
    ? `${t('alerts.alert')}: ${routineName}`
    : t('alerts.alert');

  const isCounterRoutine = useMemo(() => {
    return routineType === RoutineType.Counter;
  }, [routineType]);

  const secondAlertType = useWatch({ control, name: 'secondAlertType' });

  // Mirrors the API's supportsAutoReset: a product alert, and a group alert's
  // shared baseline, is one clock across many assets, so only a group counter
  // interval resets per asset.
  const selectedAlertTypes = [alertType, secondAlertType];
  const showAutoReset =
    !productId &&
    (isGroupAlert
      ? selectedAlertTypes.includes(AlertType.Interval)
      : selectedAlertTypes.some((type) => AUTO_RESET_ALERT_TYPES.includes(type)));
  const secondInputField = useWatch({ control, name: 'secondInputField' });
  const alertName = useWatch({ control, name: AlertInputs.alertLabel });
  const alertNamePlaceholder = useWatch({
    control,
    name: AlertInputs.alertLabelPlaceholder,
  });

  const fromDate = useWatch({ control, name: AlertInputs.startDate });
  const toDate = useWatch({ control, name: AlertInputs.endDate });
  const minCounterValue = useWatch({ control, name: AlertInputs.minValue });
  const maxCounterValue = useWatch({ control, name: AlertInputs.maxValue });
  const daysStartDate = useWatch({ control, name: AlertInputs.daysStartDate });
  const completionsValue = useWatch({ control, name: AlertInputs.completions });
  const daysSkippedValue = useWatch({ control, name: AlertInputs.daysSkipped });
  const onDateValue = useWatch({ control, name: AlertInputs.onDate });
  const dayIntervalValue = useWatch({ control, name: AlertInputs.dayInterval });
  const dayIntervalUnitValue = useWatch({
    control,
    name: AlertInputs.dayIntervalUnit,
  });

  const intervalRoutineIdValue = useWatch({
    control,
    name: AlertInputs.intervalRoutineId,
  });
  const onIntervalValue = useWatch({ control, name: AlertInputs.onInterval });
  const onIntervalStart = useWatch({
    control,
    name: AlertInputs.onIntervalStart,
  });

  // Alert time-of-day picker. Backed by its own isolated react-hook-form
  // instance (not the main `control`) so "has the user touched the pre-fill"
  // is a clean per-field dirty check — the main form's alertType-switch
  // effect below deliberately marks several fields dirty just to enable
  // Confirm, which would give a false positive if we reused it here.
  const activeScheduleDefault = useMemo(
    () =>
      getActiveScheduleDefault(alertType, secondAlertType, dayIntervalUnitValue),
    [alertType, secondAlertType, dayIntervalUnitValue]
  );
  const showSchedulePicker = !!activeScheduleDefault;

  // The alert time pairs with the PRIMARY input's date field when that input
  // has one (blackout-dates From/To layout). There is a single atTime per
  // alert, so it can only ever sit beside one date — when the primary type
  // carries no date but an additional-logic input does, it falls back to its
  // own row under Optional Settings rather than attaching to the second input.
  const primaryHasScheduleDate =
    alertType === AlertType.Date ||
    (alertType === AlertType.RecurringInterval &&
      dayIntervalUnitValue !== DayIntervalUnit.Minutes &&
      dayIntervalUnitValue !== DayIntervalUnit.Hours);
  const pairScheduleWithDate = showSchedulePicker && primaryHasScheduleDate;

  // An alert that was already saved with an explicit atTime keeps sending it
  // on every subsequent save, whether or not the picker is touched again —
  // only a pre-fill that was never customized stays default-driven.
  const hasExistingScheduleTime = !!defaultValues?.atTime;

  const scheduleTimeBaseline = useMemo(() => {
    if (defaultValues?.atTime && defaultValues?.timeZone) {
      return getWallTimeInstant(defaultValues.atTime, defaultValues.timeZone);
    }
    return activeScheduleDefault
      ? getScheduleDefaultInstant(activeScheduleDefault)
      : undefined;
  }, [defaultValues?.atTime, defaultValues?.timeZone, activeScheduleDefault]);

  const scheduleTimeForm = useForm<{ scheduleTime: string }>({
    defaultValues: { scheduleTime: scheduleTimeBaseline ?? '' },
  });
  // React Hook Form only computes `isDirty` for formState properties actually
  // read during render (a perf optimization) — destructuring it here (rather
  // than only inside onSubmit, which runs outside any render) is what makes
  // it track live instead of staying permanently false.
  const {
    formState: { isDirty: isScheduleTimeDirty },
  } = scheduleTimeForm;

  useEffect(() => {
    scheduleTimeForm.reset({ scheduleTime: scheduleTimeBaseline ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleTimeBaseline]);

  const getAlertName = useCallback(
    (alertType: AlertType, isSecondAlert: boolean) => {
      let alertValue;

      switch (alertType) {
        case AlertType.Date:
          alertValue = getValues(AlertInputs.onDate);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${t('alerts.on')} ${alertValue}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${t('alerts.on')} ${alertValue}`
            : `${defaultAlertName}`;
        case AlertType.DaysSkipped:
          alertValue = getValues(AlertInputs.daysSkipped);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${alertValue} ${t('alerts.days')} ${t(
                  'alerts.skipped'
                )}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${alertValue} ${t('alerts.days')} ${t(
                'alerts.skipped'
              )}`
            : `${defaultAlertName}`;
        case AlertType.RoutineCompletions:
          alertValue = getValues(AlertInputs.completions);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${alertValue} ${t('alerts.completions')}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${alertValue} ${t('alerts.completions')}`
            : `${defaultAlertName}`;

        case AlertType.Min:
          alertValue = getValues(AlertInputs.minValue);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${t(
                  'alerts.abovePlaceholder'
                )} ${alertValue}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${t(
                'alerts.abovePlaceholder'
              )} ${alertValue}`
            : `${defaultAlertName}`;

        case AlertType.Max:
          alertValue = getValues(AlertInputs.maxValue);
          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${t(
                  'alerts.belowPlaceholder'
                )} ${alertValue}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${t(
                'alerts.belowPlaceholder'
              )} ${alertValue}`
            : `${defaultAlertName}`;

        case AlertType.Interval: {
          alertValue = getValues(AlertInputs.onInterval);
          const routineId = getValues(AlertInputs.intervalRoutineId);
          const uom = routineId
            ? counterRoutines.find((r) => r.id === routineId)?.config.uom
            : '';

          if (isSecondAlert) {
            return alertValue
              ? ` ${t('alerts.or')} ${t('alerts.every')} ${alertValue} ${uom}`
              : '';
          }
          return alertValue
            ? `${defaultAlertName}, ${t('alerts.every')} ${alertValue} ${uom}`
            : `${defaultAlertName}`;
        }
        case AlertType.RecurringInterval: {
          const intervalValue = getValues(AlertInputs.dayInterval);
          const unitValue = getValues(AlertInputs.dayIntervalUnit);
          const unitLabel = unitValue
            ? t(`alerts.${unitValue.toLowerCase()}`, unitValue.toLowerCase())
            : '';

          if (isSecondAlert) {
            return intervalValue && unitValue
              ? ` ${t('alerts.or')} ${t(
                  'alerts.every'
                )} ${intervalValue} ${unitLabel}`
              : '';
          }
          return intervalValue && unitValue
            ? `${defaultAlertName}, ${t(
                'alerts.every'
              )} ${intervalValue} ${unitLabel}`
            : `${defaultAlertName}`;
        }
        default:
          return `${defaultAlertName}`;
      }
    },
    [counterRoutines, defaultAlertName, getValues, t]
  );

  // Programmatic sync of inputs for alert type; use shouldDirty: false so edit form isn't marked dirty on load
  const updateFormInputField = useCallback(
    (inputName: string, alertType: AlertType) => {
      const opts = { shouldDirty: false, shouldValidate: true };
      switch (alertType) {
        case AlertType.Date:
          setValue(inputName, onDateValue, opts);
          break;
        case AlertType.DaysSkipped:
          setValue(inputName, daysSkippedValue, opts);
          break;
        case AlertType.RoutineCompletions:
          setValue(inputName, completionsValue, opts);
          break;
        case AlertType.Min:
          setValue(inputName, minCounterValue, opts);
          break;
        case AlertType.Max:
          setValue(inputName, maxCounterValue, opts);
          break;
        case AlertType.Interval:
          setValue(AlertInputs.intervalRoutineId, intervalRoutineIdValue, opts);
          setShowRoutinesDropdown(true);
          setValue(inputName, onIntervalValue, opts);
          break;
        case AlertType.RecurringInterval:
          setValue(AlertInputs.dayInterval, dayIntervalValue, opts);
          setValue(AlertInputs.dayIntervalUnit, dayIntervalUnitValue, opts);
          setValue(AlertInputs.daysStartDate, daysStartDate, opts);
          setValue(inputName, dayIntervalValue, opts);
          break;

        default:
          break;
      }
    },
    [
      completionsValue,
      daysSkippedValue,
      daysStartDate,
      dayIntervalValue,
      dayIntervalUnitValue,
      intervalRoutineIdValue,
      maxCounterValue,
      minCounterValue,
      onDateValue,
      onIntervalValue,
      setValue,
    ]
  );

  const counterRoutineItems = useMemo(() => {
    if (!counterRoutines || !counterRoutines.length) return [];

    return counterRoutines.map((routine) => {
      const { config, id } = routine;

      return {
        label: `${t('alerts.Every')} ___ ${config.uom}`,
        value: id,
        count: undefined, // Counter routines don't have state.count, might come from config
        type: AlertInputs.onIntervalStart,
        uom: config.uom,
      };
    });
  }, [counterRoutines, t]);

  // update first inputField
  useEffect(() => {
    if (!alertType) return;

    updateFormInputField('inputField', alertType);

    // update custom label input
    const updatedName = getAlertName(alertType, false);
    setValue(AlertInputs.alertLabelPlaceholder, updatedName, {
      shouldDirty: false,
    });
  }, [
    alertType,
    minCounterValue,
    maxCounterValue,
    daysStartDate,
    completionsValue,
    daysSkippedValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    intervalRoutineIdValue,
    onDateValue,
    onIntervalValue,
    updateFormInputField,
    getAlertName,
    setValue,
  ]);

  // update second inputField
  useEffect(() => {
    if (!secondAlertType) return;
    updateFormInputField('secondInputField', secondAlertType);

    // update placeholder label
    const currentPlaceholder = getValues(AlertInputs.alertLabelPlaceholder);
    const strToAppend = getAlertName(secondAlertType, true);

    setValue(
      AlertInputs.alertLabelPlaceholder,
      currentPlaceholder + strToAppend,
      { shouldDirty: false }
    );
  }, [
    secondAlertType,
    minCounterValue,
    maxCounterValue,
    daysStartDate,
    completionsValue,
    daysSkippedValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    intervalRoutineIdValue,
    onDateValue,
    onIntervalValue,
    updateFormInputField,
    getValues,
    getAlertName,
    setValue,
  ]);

  // reset form on render and every time a user selects from drop picker
  useEffect(() => {
    // hide routines interval dropdown if not needed (only set state when changing to avoid infinite loop)
    const counterBasedTypes = [
      AlertType.Interval,
      AlertType.Min,
      AlertType.Max,
    ];
    if (!counterBasedTypes.includes(alertType as AlertType)) {
      setShowRoutinesDropdown((prev) => (prev ? false : prev));
    }

    // Only clear type-specific fields when the user actually changes the alert type.
    // Use setValue instead of reset() so isDirty stays true after the user switches
    // alert type — reset() treats the passed values as the new baseline (defaultValues),
    // which sets isDirty = false and keeps the Confirm button disabled (mobile requires
    // isDirty && isValid; desktop edit requires isDirty) until another field is touched.
    const defaultAlertType = defaultValues.alertType;
    if (alertType !== defaultAlertType) {
      const opts = { shouldDirty: true, shouldValidate: false } as const;
      setValue(
        'secondInputField',
        secondInputField || defaultValues.secondInputField,
        opts
      );
      // Clear inputField explicitly even though updateFormInputField (the effect above)
      // also writes it. That effect fires first in this same batch and reads watched proxy
      // values from the render snapshot — if the user previously filled a field for this
      // type (e.g. dayInterval="7"), the proxy is still non-empty in that snapshot, so
      // inputField would be set to a stale value. Clearing it here (which runs after)
      // guarantees it is empty. The backing fields cleared below will update their watched
      // values on the next render, causing updateFormInputField to re-fire and re-sync
      // inputField from the (now-undefined) proxies.
      setValue('inputField', undefined, opts);
      setValue(AlertInputs.onDate, undefined, opts);
      setValue(AlertInputs.daysSkipped, undefined, opts);
      setValue(AlertInputs.completions, undefined, opts);
      // Fall back to today when the original alert had no startDate (e.g.
      // it was a Date or DaysSkipped type) so RecurringInterval's required
      // daysStartDate validation can pass without forcing the user to
      // re-select a date they may not care about.
      setValue(
        AlertInputs.daysStartDate,
        defaultValues.daysStartDate ?? moment().format('MM/DD/YYYY'),
        opts
      );
      setValue(AlertInputs.maxValue, undefined, opts);
      setValue(AlertInputs.minValue, undefined, opts);
      setValue(AlertInputs.alertLabel, '', opts);
      setValue(AlertInputs.alertLabelPlaceholder, defaultAlertName, opts);
      setValue(AlertInputs.startDate, undefined, opts);
      setValue(AlertInputs.endDate, undefined, opts);
      setValue(AlertInputs.onInterval, undefined, opts);
      setValue(AlertInputs.intervalRoutineId, undefined, opts);
      setValue(AlertInputs.onIntervalStart, undefined, opts);
      setValue(AlertInputs.dayInterval, defaultValues.dayInterval ?? 1, opts);
      setValue(
        AlertInputs.dayIntervalUnit,
        defaultValues.dayIntervalUnit ?? DayIntervalUnit.Days,
        opts
      );
      // All setValue calls above use shouldValidate: false to batch the clears
      // without triggering per-field validation noise. Run a single full validation
      // pass now so formState.isValid reflects the cleared state immediately —
      // without this, isValid can be left stale (true or false) from the previous
      // type's values until the user touches another field.
      void trigger();
    }
  }, [
    alertType,
    defaultValues,
    setValue,
    defaultAlertName,
    secondInputField,
    trigger,
  ]);

  // Only persist the day-interval fields when the day-interval alert type is
  // actually selected. The form seeds dayInterval=1/dayIntervalUnit=Days into
  // state on every type switch (so the RecurringInterval type validates); without
  // this gate those defaults leak into every alert's config and create a spurious
  // "every 1 day" trigger that fires daily. startDate stays for DaysSkipped too.
  const isDayIntervalSelected =
    alertType === AlertType.RecurringInterval ||
    secondAlertType === AlertType.RecurringInterval;

  // Either logic slot can hold the counter interval: the "OR" picker writes
  // both to the same onInterval field, so the trigger config carries an
  // interval whichever slot it came from, and the per-asset anchors have to
  // follow it either way.
  const hasIntervalLogic =
    alertType === AlertType.Interval || secondAlertType === AlertType.Interval;

  const selectedCounterUom = useMemo(
    () =>
      (intervalRoutineIdValue
        ? counterRoutines?.find((r) => r.id === intervalRoutineIdValue)?.config
            ?.uom
        : '') || '',
    [counterRoutines, intervalRoutineIdValue]
  );

  /** Each group asset's live reading of the selected counter routine. */
  const groupAssetCurrentCounts = useMemo(() => {
    if (!groupAssets || !intervalRoutineIdValue) {
      return {} as Record<string, number>;
    }
    return groupAssets.reduce<Record<string, number>>((counts, asset) => {
      const routineState = asset.routineState?.find(
        (state) => state.workspaceRoutineId === intervalRoutineIdValue
      );
      counts[asset.id] = Number(routineState?.state?.count) || 0;
      return counts;
    }, {});
  }, [groupAssets, intervalRoutineIdValue]);

  /**
   * Where each asset's interval window starts. Never typed in: one shared
   * starting value is meaningless across a mixed group, so every asset anchors
   * on its own counter reading. Anchors already stored keep their value - an
   * asset can have been reset individually from the asset level since - so only
   * assets that joined after the alert was created get a fresh reading. Picking
   * a different counter routine invalidates all of them, since a reading of one
   * counter says nothing about another.
   */
  const assetIntervalStarts = useMemo(() => {
    if (!isGroupAlert || !hasIntervalLogic) {
      return {} as Record<string, number>;
    }
    // The group's assets are fetched separately from the alert and are not part
    // of any loading gate, so an empty list means "not known yet" as often as
    // it means "no members". Either way there is nothing to re-read, and since
    // trigger.config is rebuilt wholesale on save, computing from it would
    // erase every member's anchor.
    if (!groupAssets?.length) return initialAssetIntervalStarts;

    const config = workflowProps?.trigger?.config;
    // A reading of one counter says nothing about another, so switching the
    // counter routine invalidates every anchor, shared or per-asset.
    const counterChanged =
      !!config?.triggerCounterRoutineId &&
      config.triggerCounterRoutineId !== intervalRoutineIdValue;
    const stored = counterChanged ? {} : initialAssetIntervalStarts;
    // Alerts predating per-asset anchors carry one shared start that applied to
    // every asset. That is their real baseline: re-reading live counters
    // instead would silently move every asset's threshold on an unrelated edit
    // (a rename). Only a genuinely unanchored asset takes its current reading.
    const legacyStart = counterChanged ? undefined : config?.onIntervalStart;

    return Object.entries(groupAssetCurrentCounts).reduce<
      Record<string, number>
    >((starts, [assetId, currentCount]) => {
      const anchor = stored[assetId] ?? legacyStart;
      starts[assetId] = anchor != null ? Number(anchor) : currentCount;
      return starts;
    }, {});
  }, [
    isGroupAlert,
    hasIntervalLogic,
    groupAssets,
    workflowProps,
    intervalRoutineIdValue,
    initialAssetIntervalStarts,
    groupAssetCurrentCounts,
  ]);

  // The single shared starting value belongs to asset-level alerts only; a
  // group alert's per-asset anchors replace it, and leaving a stale one behind
  // would drive the summary's "next alert" line off the wrong baseline.
  const effectiveOnIntervalStart = isGroupAlert ? undefined : onIntervalStart;

  const defaultRoutineConfig = useMemo(() => {
    const hasAssetStarts = Object.keys(assetIntervalStarts).length > 0;
    const config = {
      workspaceRoutineId,
      daysSinceCompleted: daysSkippedValue,
      onInterval: onIntervalValue,
      onIntervalStart: effectiveOnIntervalStart,
      triggerCounterRoutineId: intervalRoutineIdValue,
      assetIntervalStarts: hasAssetStarts ? assetIntervalStarts : undefined,
      startDate:
        (isDayIntervalSelected && dayIntervalValue) || daysSkippedValue
          ? parseMMDDYYYYToDate(daysStartDate)
          : undefined,
      onCompletionCount: completionsValue,
      onDate: parseMMDDYYYYToDate(onDateValue),
      dayInterval:
        isDayIntervalSelected && dayIntervalValue
          ? parseInt(dayIntervalValue, 10)
          : undefined,
      dayIntervalUnit: isDayIntervalSelected ? dayIntervalUnitValue : undefined,
      routineType,
    };
    return Object.fromEntries(
      Object.entries(config).filter(([_, v]) => v !== undefined)
    );
  }, [
    isDayIntervalSelected,
    workspaceRoutineId,
    routineType,
    completionsValue,
    daysSkippedValue,
    daysStartDate,
    onDateValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    onIntervalValue,
    effectiveOnIntervalStart,
    intervalRoutineIdValue,
    assetIntervalStarts,
  ]);

  const counterRoutineConfig = useMemo(() => {
    const hasAssetStarts = Object.keys(assetIntervalStarts).length > 0;
    const config = {
      onInterval: onIntervalValue,
      onIntervalStart: effectiveOnIntervalStart,
      triggerCounterRoutineId: intervalRoutineIdValue,
      assetIntervalStarts: hasAssetStarts ? assetIntervalStarts : undefined,
      ceiling: minCounterValue,
      floor: maxCounterValue,
      workspaceRoutineId,
      daysSinceCompleted: daysSkippedValue,
      startDate:
        (isDayIntervalSelected && dayIntervalValue) || daysSkippedValue
          ? parseMMDDYYYYToDate(daysStartDate)
          : undefined,
      onCompletionCount: completionsValue,
      onDate: parseMMDDYYYYToDate(onDateValue),
      dayInterval:
        isDayIntervalSelected && dayIntervalValue
          ? parseInt(dayIntervalValue, 10)
          : undefined,
      dayIntervalUnit: isDayIntervalSelected ? dayIntervalUnitValue : undefined,
      routineType,
    };

    return Object.fromEntries(
      Object.entries(config).filter(([_, v]) => v !== undefined)
    );
  }, [
    isDayIntervalSelected,
    routineType,
    workspaceRoutineId,
    minCounterValue,
    maxCounterValue,
    daysStartDate,
    completionsValue,
    daysSkippedValue,
    onDateValue,
    dayIntervalValue,
    dayIntervalUnitValue,
    onIntervalValue,
    effectiveOnIntervalStart,
    intervalRoutineIdValue,
    assetIntervalStarts,
  ]);

  const onSubmit = async (data: any) => {
    const name = alertName || alertNamePlaceholder;
    const workspace = { id: workspaceId };
    const workspaceAsset = { id: workspaceAssetId };
    const workflowId = workflowProps?.id;
    const trigger = {
      type: TriggerType.Routine,
      config: counterRoutineConfig
        ? counterRoutineConfig
        : defaultRoutineConfig,
    };

    // Only write atTime/timeZone once the user has actually chosen a time.
    // An untouched pre-fill with no prior saved time stays default-driven
    // (fields omitted entirely — the no-migration property this depends on).
    // An untouched pre-fill that already had a saved time resends the exact
    // saved strings as-is, not a value re-derived from the device's current
    // timezone — otherwise merely opening/saving the form from a different
    // timezone would silently shift the alert's schedule.
    if (showSchedulePicker) {
      if (isScheduleTimeDirty) {
        const scheduleTimeValue = scheduleTimeForm.getValues('scheduleTime');
        if (scheduleTimeValue) {
          const { atTime, timeZone } =
            getDeviceAtTimeFromInstant(scheduleTimeValue);
          trigger.config.atTime = atTime;
          trigger.config.timeZone = timeZone;
        }
      } else if (hasExistingScheduleTime) {
        trigger.config.atTime = defaultValues.atTime;
        trigger.config.timeZone = defaultValues.timeZone;
      }
    }

    if (trigger.config.dayIntervalUnit && trigger.config.startDate) {
      const now = moment();
      const startDate = moment(trigger.config.startDate);

      // Preserve the date but include current time information
      let adjustedStartDate = startDate;

      switch (trigger.config.dayIntervalUnit) {
        case DayIntervalUnit.Minutes:
        case DayIntervalUnit.Hours:
          adjustedStartDate = startDate
            .hour(now.hour())
            .minute(now.minute())
            .second(now.second());
          break;
      }

      trigger.config.startDate = adjustedStartDate.toISOString();
    }

    const action = {
      type: ActionType.Alert,
      config: {
        promptWorkspaceRoutineId: workspaceRoutineId,
        autoReset: showAutoReset && !!data[AlertInputs.autoReset],
      },
    };
    const disabledBetween =
      fromDate && toDate
        ? { start: convertToISO(fromDate), end: convertToISO(toDate) }
        : undefined;

    const newAlertInput = {
      name: name,
      workspace: workspace,
      ...(workspaceAssetId && { workspaceAsset: workspaceAsset }),
      ...(productId && { product: { id: productId } }),
      ...(groupId && { group: { id: groupId } }),
      trigger: trigger,
      action: action,
      disabledBetween: disabledBetween,
    };

    const editAlertInput = {
      id: workflowId,
      name: name,
      trigger: trigger,
      action: action,
      disabledBetween: disabledBetween,
    };

    await submitAlert(isEdit ? editAlertInput : newAlertInput);
    reset();
  };

  const isWeb = Platform.OS === 'web';

  const confirmDisabled = isSubmitting || !(isDirty && isValid);
  // Desktop: disable until valid; in edit also require changes (isDirty)
  const desktopConfirmDisabled =
    isSubmitting || isValidating || !isValid || (isEdit && !isDirty);

  const showAssetIntervalWindows =
    isGroupAlert &&
    hasIntervalLogic &&
    !!groupAssets?.length &&
    !!intervalRoutineIdValue;

  /**
   * The window each asset will alert on, read-only: its own anchor and the
   * reading that trips the alert. Without this the group form gives no hint
   * that assets on one alert fire at different counter values.
   */
  const renderAssetIntervalWindows = useCallback(() => {
    if (!showAssetIntervalWindows || !groupAssets) return null;

    const interval = Number(onIntervalValue) || 0;

    return (
      <View style={styles.assetStartsContainer}>
        <Text style={styles.assetStartsLabel}>
          {t('alerts.intervalWindowPerAsset')}
        </Text>
        {groupAssets.map((asset) => {
          const start = assetIntervalStarts[asset.id] ?? 0;
          const window = interval ? `${start} → ${start + interval}` : `${start}`;

          return (
            <View key={asset.id} style={styles.assetStartRow}>
              <Text style={styles.assetStartName} numberOfLines={1}>
                {asset.asset?.name || asset.id}
              </Text>
              <Text style={styles.assetStartWindow}>
                {selectedCounterUom ? `${window} ${selectedCounterUom}` : window}
              </Text>
            </View>
          );
        })}
      </View>
    );
  }, [
    showAssetIntervalWindows,
    groupAssets,
    assetIntervalStarts,
    onIntervalValue,
    selectedCounterUom,
    t,
  ]);

  // Defer setState so picker/dropdown don't trigger update-during-render. Use setTimeout (not requestAnimationFrame) to avoid "Illegal invocation" on web.
  const defer = useCallback((fn: () => void) => {
    setTimeout(fn, 0);
  }, []);
  const setAdditionalLogicDeferred = useCallback(
    (value: boolean) => defer(() => setAdditionalLogic(value)),
    [defer]
  );
  const setShowRoutinesDropdownDeferred = useCallback(
    (value: boolean) => defer(() => setShowRoutinesDropdown(value)),
    [defer]
  );
  const onDropdownStateChangeDeferred = useCallback(
    (isOpen: boolean) => defer(() => setIsDropdownOpen(isOpen)),
    [defer]
  );

  const cropCircleBackButton = (
    <Button
      variant="rounded"
      state="active"
      onPress={() => {
        if (Platform.OS === 'web' && onClose) {
          onClose();
        } else if (router.canGoBack()) {
          router.back();
        } else {
          router.replace('/');
        }
      }}
      style={styles.cropCircleBackButton}
      leftIcon={
        // `state="active"` fills with `surfaceInverse`, which is white in dark
        // mode, so the glyph has to be the token paired with it. `textOnBrand`
        // is a constant white and vanished on the dark-mode fill.
        <Icon name="Chevron Left" size={24} color={Colors.role.textOnInverse} />
      }
    />
  );

  const cropCircleResetButton = (
    <Button
      variant="rounded"
      state="active"
      onPress={() => {
        reset();
        scheduleTimeForm.reset({ scheduleTime: scheduleTimeBaseline ?? '' });
      }}
      style={styles.cropCircleResetButton}
      leftIcon={
        <Icon name="Reset" size={24} color={Colors.role.textOnInverse} />
      }
    />
  );

  const inlineSchedulePicker = pairScheduleWithDate && (
    <DatePickerSelector
      mode="time"
      name="scheduleTime"
      control={scheduleTimeForm.control}
      setValue={scheduleTimeForm.setValue}
      label={t('alerts.alertTime')}
      placeholder="00:00"
      containerStyle={styles.inlineScheduleTimePicker}
    />
  );

  const schedulePicker = showSchedulePicker && !pairScheduleWithDate && (
    <View style={styles.scheduleTimeSection}>
      <Text style={styles.scheduleTimeLabel}>{t('alerts.alertTime')}</Text>
      <DatePickerSelector
        mode="time"
        name="scheduleTime"
        control={scheduleTimeForm.control}
        setValue={scheduleTimeForm.setValue}
        placeholder="00:00"
        containerStyle={styles.scheduleTimePicker}
      />
    </View>
  );

  return (
    <FormContext.Provider value={formMethods}>
      {isWeb ? (
        <View style={styles.webRoot}>
          <View
            style={[
              styles.container,
              styles.webScrollWrap,
              { backgroundColor: Colors.role.canvas },
            ]}
          >
            <ScrollView
              style={styles.webScrollView}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.scrollViewContainer}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled={true}
            >
              {availableRoutines &&
                availableRoutines.length > 0 && (
                  <View style={styles.routineSelectionContainer}>
                    <Text style={styles.routineSelectionLabel}>
                      {t('alerts.selectRoutineToPrompt')}
                    </Text>
                    <ControlledSelect
                      name="selectedRoutine"
                      control={control}
                      label={t('alerts.selectRoutineToPrompt')}
                      items={availableRoutines.map((routine) => ({
                        label:
                          routine.name ||
                          routine.config?.routineButtonText ||
                          'Unnamed Routine',
                        value: routine.id,
                      }))}
                      disabled={!!routine}
                      onSelectItem={(item: string | null) => {
                        if (item && typeof item === 'string') {
                          setValue('selectedRoutine', item, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                      dropDownPickerProps={{
                        ArrowDownIconComponent: () => (
                          <MaterialCommunityIcons
                            name="chevron-down"
                            size={28}
                          />
                        ),
                        ArrowUpIconComponent: () => (
                          <MaterialCommunityIcons name="chevron-up" size={28} />
                        ),
                        style: styles.routineDropdown,
                        placeholderStyle: styles.routinePlaceholderLabel,
                      }}
                    />
                  </View>
                )}
              {counterRoutineItems && (
                <AlertLogicPicker
                  control={control}
                  alertType={alertType}
                  secondAlertType={secondAlertType}
                  additionalLogic={additionalLogic}
                  isCounterRoutine={isCounterRoutine}
                  setAdditionalLogic={setAdditionalLogicDeferred}
                  showRoutinesDropdown={showRoutinesDropdown}
                  setShowRoutinesDropdown={setShowRoutinesDropdownDeferred}
                  counterRoutineItems={counterRoutineItems}
                  openDropdownOnLoad={openDropdownOnLoad}
                  hideIntervalStart={isGroupAlert}
                  showAutoReset={showAutoReset}
                  belowLogicSlot={renderAssetIntervalWindows()}
                  schedulePickerSlot={inlineSchedulePicker || undefined}
                />
              )}
              {schedulePicker}
            </ScrollView>
            <View style={styles.webContentActions}>
              {renderBottomComponent ? (
                renderBottomComponent(
                  handleSubmit(onSubmit),
                  desktopConfirmDisabled
                )
              ) : (
                <View style={styles.webButtonRow}>
                  {cropCircleBackButton}
                  <Button
                    state="active"
                    onPress={handleSubmit(onSubmit)}
                    disabled={confirmDisabled}
                    style={styles.webConfirmButton}
                  >
                    {t('alerts.confirm')}
                  </Button>
                  {cropCircleResetButton}
                </View>
              )}
              {isEdit && (
                <Button
                  variant="rectangular"
                  state="outline"
                  style={styles.deleteAlertButton}
                  onPress={deleteAlert}
                  size="default"
                >
                  {t('alerts.deleteAlert')}
                </Button>
              )}
            </View>
          </View>
        </View>
      ) : (
        <>
          <View style={styles.cropCircleHeader}>
            <Text style={styles.cropCircleHeaderText}>
              {isEdit ? t('alerts.editAlert') : t('alerts.newAlert')}
            </Text>
          </View>
          <KeyboardSafeView style={styles.cropCircleContainer}>
            <ScrollView
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={[
                styles.cropCircleScrollContent,
                isDropdownOpen && {
                  paddingBottom: 120 + DROPDOWN_MAX_HEIGHT,
                },
              ]}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled={true}
            >
              {availableRoutines &&
                availableRoutines.length > 0 &&
                Platform.OS === 'web' && (
                  <View style={styles.cropCircleSection}>
                    <Text style={styles.cropCircleSectionLabel}>
                      {t('alerts.selectRoutineToPrompt')}
                    </Text>
                    <ControlledSelect
                      name="selectedRoutine"
                      control={control}
                      label={t('alerts.selectRoutineToPrompt')}
                      items={availableRoutines.map((routine) => ({
                        label:
                          routine.name ||
                          routine.config?.routineButtonText ||
                          'Unnamed Routine',
                        value: routine.id,
                      }))}
                      disabled={true}
                      onSelectItem={(item: string | null) => {
                        if (item && typeof item === 'string') {
                          setValue('selectedRoutine', item, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                      dropDownPickerProps={getCropCircleDropDownPickerProps()}
                    />
                  </View>
                )}
              {counterRoutineItems && (
                <View style={styles.cropCircleSection}>
                  <Text style={styles.cropCircleSectionLabel}>
                    {t('alerts.setAlertLogic')}
                  </Text>
                  <AlertLogicPicker
                    control={control}
                    alertType={alertType}
                    secondAlertType={secondAlertType}
                    additionalLogic={additionalLogic}
                    isCounterRoutine={isCounterRoutine}
                    setAdditionalLogic={setAdditionalLogicDeferred}
                    showRoutinesDropdown={showRoutinesDropdown}
                    setShowRoutinesDropdown={setShowRoutinesDropdownDeferred}
                    counterRoutineItems={counterRoutineItems}
                    openDropdownOnLoad={openDropdownOnLoad}
                    hideIntervalStart={isGroupAlert}
                    showAutoReset={showAutoReset}
                    belowLogicSlot={renderAssetIntervalWindows()}
                    onDropdownStateChange={onDropdownStateChangeDeferred}
                    schedulePickerSlot={inlineSchedulePicker || undefined}
                  />
                </View>
              )}
              {schedulePicker}
              {isEdit && (
                <Button
                  onPress={deleteAlert}
                  state="outline"
                  variant="rounded"
                  style={{ marginTop: tokens.spacing.lg }}
                >
                  {t('alerts.deleteAlert')}
                </Button>
              )}
            </ScrollView>
          </KeyboardSafeView>
          <BottomComponentBar
            backButton={cropCircleBackButton}
            onSave={handleSubmit(onSubmit)}
            rightPressDisabled={confirmDisabled}
            saveLabel={t('alerts.confirm')}
            right={cropCircleResetButton}
          />
        </>
      )}
    </FormContext.Provider>
  );
};

/** Shared by the web form's section headers so they cannot drift apart. */
const sectionLabelText = {
  fontSize: 16,
  fontWeight: '500' as const,
  marginBottom: tokens.spacing.sm,
};

const styles = themedStyleSheet((role) => ({
  webRoot: {
    flex: 1,
    minHeight: 0,
  },
  webScrollWrap: {
    flex: 1,
    minHeight: 0,
  },
  container: {
    flex: 1,
    flexGrow: 1,
    backgroundColor: role.surfaceHover,
    padding: tokens.spacing.lg,
  },
  scrollViewContainer: {
    paddingBottom: tokens.spacing.md,
  },
  webScrollView: {
    flex: 1,
  },
  webContentActions: {
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.lg,
    gap: tokens.spacing.sm,
  },
  webButtonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  webConfirmButton: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    marginHorizontal: tokens.spacing.xs,
  },
  routineSelectionContainer: {
    marginBottom: 20,
    backgroundColor: role.surface,
    borderRadius: 8,
    padding: 16,
    zIndex: 1000,
    elevation: 1000,
  },
  routineSelectionLabel: {
    ...sectionLabelText,
    color: role.textStrong,
  },
  routineDropdown: {
    borderWidth: 1,
    borderColor: role.borderEmphasis,
    height: 52,
    fontSize: 16,
    fontWeight: '400',
    color: role.textStrong,
  },
  routinePlaceholderLabel: {
    fontSize: 16,
    fontWeight: '400',
    color: role.fillMuted,
  },
  deleteAlertButton: {
    width: '100%',
    borderWidth: 0,
  },
  cropCircleHeader: {
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing.md,
    backgroundColor: role.canvas,
  },
  cropCircleHeaderText: {
    ...tokens.typography.body.medium,
    fontWeight: fontWeights.semibold,
    color: role.textStrong,
  },
  cropCircleContainer: {
    flex: 1,
    flexGrow: 1,
    backgroundColor: role.canvas,
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.sm,
  },
  cropCircleScrollContent: {
    flexGrow: 1,
    paddingBottom: 120,
  },
  cropCircleSection: {
    marginBottom: tokens.spacing.lg,
  },
  cropCircleSectionLabel: {
    ...tokens.typography.body.small,
    fontWeight: fontWeights.semibold,
    color: role.textStrong,
    marginBottom: tokens.spacing.sm,
  },
  scheduleTimeSection: {
    marginBottom: tokens.spacing.lg,
  },
  scheduleTimeLabel: {
    ...tokens.typography.body.small,
    fontWeight: fontWeights.semibold,
    color: role.textStrong,
    marginBottom: tokens.spacing.sm,
  },
  // Half-row time field paired with a date: matches cropCircleInputContainer's
  // 52px box and drops the picker's default full width. minWidth 0 is required
  // or the input's intrinsic width claims more than its share of the row.
  inlineScheduleTimePicker: {
    flex: 1,
    width: 'auto',
    minWidth: 0,
    height: 52,
    paddingTop: 0,
    paddingBottom: 0,
    justifyContent: 'center',
  },
  scheduleTimePicker: {
    minHeight: 48,
  },
  cropCircleBackButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginLeft: 16,
  },
  cropCircleResetButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginRight: 16,
  },
  // Same card and label treatment as the routine selector above it, so the
  // form reads as one column of sections rather than a panel with a footnote.
  assetStartsContainer: {
    marginTop: 20,
    backgroundColor: role.surface,
    borderRadius: 8,
    padding: 16,
  },
  assetStartsLabel: {
    ...sectionLabelText,
    color: role.textStrong,
  },
  assetStartRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: tokens.spacing.sm,
  },
  assetStartName: {
    ...tokens.typography.body.small,
    color: role.textStrong,
    flex: 1,
    marginRight: tokens.spacing.sm,
  },
  assetStartWindow: {
    ...tokens.typography.body.small,
    color: role.textSecondary,
    flexShrink: 0,
  },
}));
